<script setup>
/* "Spieler löschen" — nur für admin, nur im Bearbeiten-Modus der Spielerdaten (SectorPass.vue).
 *
 * Gedacht für Tippfehler und Doppelanlagen (Einzelfälle). Für Abgänge sind die Status
 * "transferiert" und "freigegeben" der richtige Weg; der Rückfragetext sagt das.
 *
 * Bestätigung: Der Nachname muss eingetippt werden (ConfirmDialog, requireText), erst dann
 * ist der rote Knopf frei. Begründung gegenüber einem bloßen roten Knopf: Das Löschen nimmt Daten,
 * Dateien und Verträge unwiderruflich mit, es gibt keinen Papierkorb; ein Knopf lässt sich im
 * Reflex treffen, das Eintippen des Namens erzwingt, dass man gelesen hat, welcher Spieler es
 * ist (bei Doppelanlagen derselbe Name — dann aber bewusst der richtige Eintrag). Enter bestätigt
 * nie (ConfirmDialog).
 *
 * Das Löschen selbst läuft über usePlayers().remove → domain/playerDeletion.js. Die Oberfläche
 * entscheidet nichts über Rechte: Die Datenbank erlaubt das Löschen nur admin.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '../composables/useAuth.js';
import { usePlayers } from '../composables/usePlayers.js';
import { useConfirm } from '../composables/useConfirm.js';
import { useSnackbar } from '../composables/useSnackbar.js';
import { deleteConfirmWord, deleteErrorKey } from '../domain/playerDeletion.js';
import { fullName } from '../domain/format.js';

const props = defineProps({
  player: { type: Object, required: true },
});

const { t } = useI18n();
const { canDeletePlayers } = useAuth();
const { remove } = usePlayers();
const { confirm } = useConfirm();
const { show } = useSnackbar();

const busy = ref(false);
const error = ref(null);

/* Ein noch nicht gespeicherter Spieler (Entwurf) hat nichts zu löschen */
const visible = computed(
  () => canDeletePlayers() && !!props.player.id && props.player._isNew !== true
);

async function onDelete() {
  const p = props.player;
  const word = deleteConfirmWord(p);
  const ok = await confirm({
    title: t('confirm.deletePlayerTitle'),
    message: t('confirm.deletePlayer', { name: fullName(p) || word }),
    detail: t('confirm.deletePlayerHint', { transferred: t('status.transferred'), released: t('status.released') }),
    requireText: word,
    requireLabel: t('confirm.deletePlayerType', { word }),
    confirmLabel: t('confirm.deletePlayerLabel'),
  });
  if (!ok) return;

  busy.value = true;
  error.value = null;
  try {
    const result = await remove(p, { confirmed: true });
    if (result.filesFailed.length) {
      show(t('player.deletedFilesLeft', { count: result.filesFailed.length }), 'warning');
    } else if (result.notificationsFailed) {
      show(t('player.deletedNotesLeft'), 'warning');
    } else {
      show(t('player.deleted'), 'success');
    }
  } catch (e) {
    /* Vor dem Löschen der Zeile gescheitert: nichts ist verändert */
    error.value = deleteErrorKey(e && e.message);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div v-if="visible" class="dp">
    <button type="button" class="dp-btn" :disabled="busy" data-testid="delete-player" @click="onDelete">
      {{ busy ? $t('common.loading') : $t('player.deletePlayer') }}
    </button>
    <span class="dp-hint">{{ $t('player.deletePlayerHint') }}</span>
    <p v-if="error" class="dp-error" role="alert">{{ $t(`errors.${error}`) }}</p>
  </div>
</template>

<style scoped>
/* Unauffällig: eine kleine Textschaltfläche ganz unten, erst beim Darüberfahren/Fokus rot hinterlegt.
   Farben nur aus dem AFH-System (--red, --red-bg, --dim). */
.dp {
  width: 100%;
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px solid var(--border-soft);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
}
.dp-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 7px;
  color: var(--red);
  font-size: var(--fs-xs);
  padding: 6px 10px;
  cursor: pointer;
}
.dp-btn:hover,
.dp-btn:focus-visible {
  background: var(--red-bg);
  border-color: var(--red);
  outline: none;
}
.dp-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.dp-hint {
  font-size: var(--fs-2xs);
  color: var(--dim);
}
.dp-error {
  width: 100%;
  font-size: var(--fs-xs);
  color: var(--red);
}
</style>
